import { Browser } from 'library/ts/model/enums/system/Browser';
import { IOSDevice } from 'library/ts/model/enums/system/IOSDevice';
import { OperatingSystem } from 'library/ts/model/enums/system/OperatingSystem';
import { VideoType } from 'library/ts/model/enums/system/VideoType';
import { TrueType } from 'library/ts/utilities/modules/enums/TrueType';
export class Capabilities {
    #utils;
    #videoElement;
    #audioElement;
    #canvasElement;
    #isChrome;
    #isPrivateMode = false;
    #os = null;
    #osVersion;
    #browser;
    #browserVersion;
    #videoFormatSupport = {
        OGG: null,
        H264: null,
        WEBM: null,
        VP9: null,
        HLS: null,
        MPEG4: null
    };
    constructor(utilities) {
        this.#utils = utilities;
        this.#videoElement = document.createElement('video');
        this.#audioElement = new Audio();
        this.#canvasElement = document.createElement('canvas');
        this.#isChrome = (!!window.chrome
            &&
                (this.utils.Type.isArray(this.userAgent.match(/Chrom(e|ium)\/(\S+)/))));
    }
    get name() {
        return 'Capabilities';
    }
    get utils() {
        return this.#utils;
    }
    get STRING() {
        return this.utils.Object.enumToObject(OperatingSystem);
    }
    get userAgent() {
        return navigator.userAgent;
    }
    get videoFormatSupport() {
        return this.#videoFormatSupport;
    }
    get h264() {
        return '' !== (this.#videoElement.canPlayType('video/mp4; codecs="avc1.42001e,mp4a.40.2"')
            &&
                this.#videoElement.canPlayType('video/mp4; codecs="avc1.42001e,mp4a.40.2"'));
    }
    get mpeg4() {
        return '' !== this.#videoElement.canPlayType('video/mp4; codecs="mp4v.20.8"');
    }
    get ogg() {
        return '' !== this.#videoElement.canPlayType('video/ogg; codecs="theora"');
    }
    get webm() {
        return '' !== this.#videoElement.canPlayType('video/webm; codecs="vp8, vorbis"');
    }
    get canvasSupport() {
        return !!this.#canvasElement.getContext;
    }
    get dolbyDigitalPlus() {
        return this.dolbyDigitalPlusSupport();
    }
    get isOpera() {
        return Boolean(window.opera
            ||
                window.opr
            ||
                (this.userAgent.indexOf(' OPR/') > -1)
            ||
                (this.userAgent.indexOf(' Coast/') > -1)
            ||
                (this.userAgent.indexOf(' OPiOS/') > -1));
    }
    get isOperaHigh() {
        return ((this.userAgent.indexOf('OPR/') > -1)
            &&
                (this.userAgent.indexOf('Mobile') > -1)
            &&
                (this.userAgent.indexOf('Presto/') < 0));
    }
    get isOperaExtreme() {
        return ((this.userAgent.indexOf('Opera Mini/') > -1)
            &&
                (this.userAgent.indexOf('Presto/') > -1));
    }
    get isChrome() {
        return this.#isChrome;
    }
    get isChromeIOS() {
        return /CriOS/i.test(navigator.userAgent) && /iphone|ipod|ipad/i.test(navigator.userAgent);
    }
    get isFirefox() {
        return this.userAgent.indexOf('Firefox/') != -1 && this.userAgent.indexOf('Seamonkey/') === -1;
    }
    get isPrivateMode() {
        return this.#isPrivateMode;
    }
    get isSafari() {
        return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    }
    get isBlink() {
        return (this.isChrome || this.isOpera) && !!window.CSS;
    }
    get android() {
        return !!(this.userAgent.match(new RegExp('android', 'i')));
    }
    get bb6() {
        return /BlackBerry.*Version\/6\.0/.test(this.userAgent);
    }
    get devicePixelRatio() {
        return window.devicePixelRatio || 1;
    }
    get screenWidth() {
        return screen.width * this.devicePixelRatio;
    }
    get screenHeight() {
        return screen.height * this.devicePixelRatio;
    }
    get os() {
        return this.#os;
    }
    get osVersion() {
        return this.#osVersion;
    }
    get browser() {
        return this.#browser;
    }
    get browserVersion() {
        return this.#browserVersion;
    }
    get isMobile() {
        return {
            Android: () => {
                return this.utils.Type.isArray(this.userAgent.match(/Android/i));
            },
            BlackBerry: () => {
                return this.utils.Type.isArray(this.userAgent.match(/BlackBerry/i));
            },
            iOS: () => {
                return this.utils.Type.isArray(this.userAgent.match(/iPhone|iPad|iPod/i));
            },
            Opera: () => {
                return this.utils.Type.isArray(this.userAgent.match(/Opera Mini|Opera Mobi/i));
            },
            Windows: () => {
                return this.utils.Type.isArray(this.userAgent.match(/IEMobile/i));
            },
            any: () => {
                return (this.isMobile.Android() === true
                    ||
                        this.isMobile.BlackBerry() === true
                    ||
                        this.isMobile.iOS() === true
                    ||
                        this.isMobile.Opera() === true
                    ||
                        this.isMobile.Windows() === true);
            }
        };
    }
    get chromeVersion() {
        return this.getChromeVersion();
    }
    get safariVersion() {
        return this.getSafariVersion();
    }
    get isIE() {
        return (this.userAgent.indexOf('MSIE ') > 0 || this.userAgent.indexOf('Trident/') > 0);
    }
    get ieVersion() {
        return this.getIEVersion();
    }
    get isEdge() {
        return (this.userAgent.indexOf('Edge/') > -1 || this.userAgent.indexOf('Edg/') > -1);
    }
    get edgeVersion() {
        return this.getEdgeVersion();
    }
    get firefoxVersion() {
        return this.getFirefoxVersion();
    }
    get operaVersion() {
        return this.getOperaVersion();
    }
    get isDesktop() {
        return !this.isMobile.any();
    }
    get isTouchDevice() {
        return ('ontouchstart' in window || window.DocumentTouch && document instanceof window.DocumentTouch);
    }
    get hasTouch() {
        return window.matchMedia('(any-pointer: coarse)').matches;
    }
    get hasMouse() {
        return window.matchMedia('(pointer:fine)').matches;
    }
    get ios() {
        return this.iOS().ios;
    }
    get iosVersion() {
        return this.iOS().version;
    }
    dolbyDigitalPlusSupport = () => {
        let supportDDPlus = false;
        if (this.#audioElement.canPlayType('audio/mp4;codecs="ec-3"') !== '') {
            if ((this.userAgent.indexOf('CPU iPhone OS 10_0') !== -1
                || this.userAgent.indexOf('CPU OS 10_0') !== -1)
                && this.userAgent.indexOf('Safari') !== -1
                && this.userAgent.indexOf('Version/10') !== -1) {
                supportDDPlus = true;
            }
            if (this.userAgent.indexOf('Mac OS X 10_12') !== -1
                && this.userAgent.indexOf('Safari') !== -1
                && this.userAgent.indexOf('Version/10') !== -1) {
                supportDDPlus = true;
            }
            if (this.userAgent.indexOf('Edge') !== -1)
                supportDDPlus = true;
            if (this.userAgent.indexOf('Windows Phone 10') !== -1)
                supportDDPlus = false;
        }
        return supportDDPlus;
    };
    iOS = () => {
        const iDevices = [
            'iPad Simulator',
            'iPhone Simulator',
            'iPod Simulator',
            'iPad',
            'iPhone',
            'iPod'
        ];
        const out = {
            ios: false,
            version: undefined,
            device: undefined
        };
        while (iDevices.length) {
            let device = iDevices.pop();
            if (this.userAgent.indexOf(device) > -1) {
                if (device === 'iPad') {
                    device = IOSDevice.IPAD;
                }
                if (device === 'iPhone') {
                    device = IOSDevice.IPHONE;
                }
                out.ios = true;
                out.device = device;
                out.version = parseFloat(('' + (/CPU.*OS ([0-9_]{1,5})|(CPU like).*AppleWebKit.*Mobile/i.exec(this.userAgent) || [0, ''])[1])
                    .replace(TrueType.UNDEFINED, '3_2').replace('_', '.').replace('_', '')) || undefined;
                return out;
            }
        }
        return out;
    };
    isIOS = () => {
        return (/iP(hone|od|ad)/.test(this.userAgent));
    };
    iOSVersion = () => {
        if (window.MSStream) {
            return false;
        }
        const match = (this.userAgent.match(/\b[0-9]+_[0-9]+(?:_[0-9]+)?\b/) || [''])[0].split('_');
        let version;
        if (this.isIOS() && !this.utils.Type.isNullOrUndefined(match)) {
            version = [
                parseInt(match[0], 10),
                parseInt(match[1], 10),
                parseInt(match[2] || 0, 10)
            ];
            return parseFloat(version.join('.'));
        }
        return false;
    };
    getAndroidVersion = () => {
        const match = this.userAgent.toLowerCase().match(/android\s([0-9\.]*)/);
        return match ? match[1] : false;
    };
    #getOS = () => {
        return new Promise((resolve, reject) => {
            let operatingSystem = null;
            let osVersion;
            if (/Mac/.test(this.userAgent) === true) {
                operatingSystem = OperatingSystem.MAC_OS;
                if (this.utils.Type.isArray(this.userAgent.match(/iPhone|iPad|iPod/i)) === true) {
                    operatingSystem = OperatingSystem.IOS;
                    osVersion = this.iOSVersion();
                }
                else {
                    if (/Android/.test(this.userAgent)) {
                        operatingSystem = OperatingSystem.ANDROID;
                        osVersion = this.getAndroidVersion();
                    }
                    if (/Mac OS X/.test(this.userAgent)) {
                        const baseOSXVersion = 9;
                        for (let i = 0; i < 30; i++) {
                            const dynamicOSXVersion = (baseOSXVersion + i);
                            const osxVersionRegEx = new RegExp('Mac OS X (' + dynamicOSXVersion + '[\\.\\_\\d]+)');
                            const osxVersionArray = osxVersionRegEx.exec(this.userAgent);
                            if (this.utils.Type.isNull(osxVersionArray) === false) {
                                if (this.utils.Type.isArray(osxVersionArray)
                                    &&
                                        osxVersionArray.length > 1
                                    &&
                                        this.utils.Type.isString(osxVersionArray[1])) {
                                    osVersion = osxVersionArray[1].split('_').join('.');
                                }
                                break;
                            }
                        }
                    }
                }
            }
            else if (/Windows/.test(this.userAgent)) {
                operatingSystem = OperatingSystem.WINDOWS;
                const regWindows = /Windows\ NT\ ([^\s]+)[;\)]/;
                const re = new RegExp(regWindows);
                const test = re.exec(this.userAgent);
                if (this.utils.Type.isArray(test)) {
                    const winVersionNumber = test[1];
                    switch (winVersionNumber) {
                        case '5.1':
                            operatingSystem = OperatingSystem.WINDOWS_XP;
                            osVersion = 'XP';
                            break;
                        case '6.0':
                            operatingSystem = OperatingSystem.WINDOWS_VISTA;
                            osVersion = 'Vista';
                            break;
                        case '6.1':
                            operatingSystem = OperatingSystem.WINDOWS_7;
                            osVersion = '7';
                            break;
                        case '6.2':
                            operatingSystem = OperatingSystem.WINDOWS_8;
                            osVersion = '8';
                            break;
                        case '6.3':
                            operatingSystem = OperatingSystem.WINDOWS_8_1;
                            osVersion = '8.1';
                            break;
                        case '10.0':
                            operatingSystem = OperatingSystem.WINDOWS_10;
                            osVersion = '10';
                            break;
                        case '11.0':
                        case '12.0':
                        case '13.0':
                            operatingSystem = OperatingSystem.WINDOWS_11;
                            osVersion = '11';
                            break;
                    }
                }
                if (this.utils.Type.isDefined(navigator['userAgentData'])
                    &&
                        this.utils.Type.isDefined(navigator['userAgentData'].getHighEntropyValues)) {
                    navigator['userAgentData'].getHighEntropyValues(['platformVersion'])
                        .then((ua) => {
                        if (navigator['userAgentData'].platform === 'Windows') {
                            const majorPlatformVersion = parseInt(ua.platformVersion.split('.')[0]);
                            if (majorPlatformVersion >= 13) {
                                operatingSystem = OperatingSystem.WINDOWS_11;
                                osVersion = '11';
                                this.#os = operatingSystem;
                                this.#osVersion = osVersion;
                                resolve({ msg: 'done', success: true, result: operatingSystem });
                            }
                            else if (majorPlatformVersion > 0) {
                            }
                            else {
                            }
                        }
                        else {
                        }
                    });
                }
            }
            else if (/Android/.test(this.userAgent)) {
                operatingSystem = OperatingSystem.ANDROID;
                osVersion = this.getAndroidVersion();
            }
            else if (!operatingSystem && /Linux/.test(this.userAgent)) {
                operatingSystem = OperatingSystem.LINUX;
            }
            this.#os = operatingSystem;
            this.#osVersion = osVersion;
            resolve({ msg: 'done', success: true, result: operatingSystem });
        });
    };
    getOperaVersion = () => {
        const output = {
            version: undefined,
            majorVersion: undefined
        };
        if (this.isOpera === true) {
            const browser = /Opera|OPR\/(\S+)/.exec(this.userAgent);
            if (this.utils.Type.isArray(browser) && browser.length > 1) {
                output.version = browser[1];
                if (output.version.indexOf('.') > -1) {
                    const versionArray = output.version.split('.');
                    if (this.utils.Type.isArray(versionArray) && versionArray.length > 0) {
                        output.majorVersion = parseInt(versionArray[0]);
                    }
                }
                else {
                    output.majorVersion = output.version;
                }
            }
        }
        return output;
    };
    getFirefoxVersion = () => {
        if (this.isFirefox === false)
            return undefined;
        return {
            version: this.userAgent.split('Firefox/')[1],
            majorVersion: parseFloat(this.userAgent.split('Firefox/')[1])
        };
    };
    getSafariVersion = () => {
        const output = {
            version: undefined,
            majorVersion: undefined
        };
        if (this.isSafari === true) {
            const browser = /Version\/(\S+).*Safari\//.exec(this.userAgent);
            if (this.utils.Type.isArray(browser) && browser.length > 1) {
                output.version = browser[1];
                if (output.version.indexOf('.') > -1) {
                    const versionArray = output.version.split('.');
                    if (this.utils.Type.isArray(versionArray) && versionArray.length > 0) {
                        output.majorVersion = parseInt(versionArray[0]);
                    }
                }
                else {
                    output.majorVersion = parseInt(output.version);
                }
            }
        }
        return output;
    };
    getIEVersion = () => {
        const msie = this.userAgent.indexOf('MSIE ');
        if (msie > 0) {
            return parseInt(this.userAgent.substring(msie + 5, this.userAgent.indexOf('.', msie)), 10);
        }
        const trident = this.userAgent.indexOf('Trident/');
        if (trident > 0) {
            const rv = this.userAgent.indexOf('rv:');
            return parseInt(this.userAgent.substring(rv + 3, this.userAgent.indexOf('.', rv)), 10);
        }
        return undefined;
    };
    getEdgeVersion = () => {
        const output = {
            majorVersion: undefined,
            full: undefined,
            chromium: false
        };
        const edge = (this.userAgent.indexOf('Edge/') > -1 || this.userAgent.indexOf('Edg/') > -1);
        if (edge > 0) {
            let edgeToken = 'Edge/';
            if (this.userAgent.indexOf(edgeToken) === -1)
                edgeToken = 'Edg/';
            let fullVersion = undefined;
            const temp = this.userAgent.split(edgeToken);
            if (temp.length > 0)
                fullVersion = temp[1];
            output.majorVersion = parseInt(fullVersion);
            output.full = fullVersion;
            output.chromium = (edgeToken === 'Edg/');
        }
        return output;
    };
    getChromeVersion = () => {
        if (this.isChrome === false)
            return undefined;
        const rawVersion = this.userAgent.match(/Chrom(e|ium)\/(\S+)/);
        let fullVersion;
        if (this.utils.Type.isArray(rawVersion)) {
            fullVersion = rawVersion[0].split('/')[1];
        }
        return {
            version: fullVersion,
            majorVersion: parseFloat(fullVersion)
        };
    };
    #testPrivateMode = (callback) => {
        const on = () => callback(true);
        const off = () => callback(false);
        const testLocalStorage = () => {
            try {
                if (localStorage.length) {
                    off();
                }
                else {
                    localStorage.x = 1;
                    localStorage.removeItem('x');
                    off();
                }
            }
            catch (e) {
                navigator.cookieEnabled ? on() : off();
            }
        };
        if (window.webkitRequestFileSystem) {
            return void window.webkitRequestFileSystem(0, 0, off, on);
        }
        if ('MozAppearance' in document.documentElement.style) {
            const db = indexedDB.open('test');
            db.onerror = on;
            db.onsuccess = off;
            return void 0;
        }
        if (/constructor/i.test(window.HTMLElement)) {
            return testLocalStorage();
        }
        if (!window.indexedDB && (window.PointerEvent || window['MSPointerEvent'])) {
            return on();
        }
        return off();
    };
    detect = () => {
        return new Promise((resolve, reject) => {
            const supportsVideoType = (type) => {
                const formats = {
                    OGG: 'video/ogg; codecs="theora"',
                    H264: 'video/mp4; codecs="avc1.42E01E"',
                    WEBM: 'video/webm; codecs="vp8, vorbis"',
                    VP9: 'video/webm; codecs="vp9"',
                    HLS: 'video/mp4; codecs="avc1.42E01E,mp4a.40.2"',
                    MPEG4: 'video/mp4; codecs="mp4v.20.8"'
                };
                const typeToCheck = formats[type] || type;
                return this.#videoElement.canPlayType(typeToCheck);
            };
            for (const videoTypeKey in VideoType) {
                this.#videoFormatSupport[videoTypeKey] = supportsVideoType(VideoType[videoTypeKey]);
            }
            if (this.userAgent.indexOf('Edge/') > -1 || this.userAgent.indexOf('Edg/') > -1)
                this.#isChrome = false;
            this.#getOS().then((data) => {
                if (this.isChrome) {
                    this.#browser = Browser.CHROME;
                    this.#browserVersion = this.chromeVersion.majorVersion;
                }
                else if (this.isFirefox) {
                    this.#browser = Browser.FIREFOX;
                    this.#browserVersion = this.firefoxVersion.majorVersion;
                }
                else if (this.isSafari) {
                    this.#browser = Browser.SAFARI;
                    this.#browserVersion = this.safariVersion.majorVersion;
                }
                else if (this.isEdge) {
                    this.#browser = Browser.EDGE;
                    this.#browserVersion = this.edgeVersion.majorVersion;
                }
                else if (this.isIE) {
                    this.#browser = Browser.IE;
                    this.#browserVersion = this.ieVersion;
                }
                else if (this.isOpera) {
                    this.#browser = Browser.OPERA;
                    this.#browserVersion = this.operaVersion.majorVersion;
                }
                this.#testPrivateMode((is_private) => {
                    this.#isPrivateMode = is_private;
                    resolve({ msg: 'done', success: true, result: this });
                });
            });
        });
    };
}
